import React, { useState } from "react";
import MInputField, {
  MInputFieldProps,
} from "@/components/ui/atoms/MInputField";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import { IconCalendarWeekFilled } from "@tabler/icons-react";
import { inputFieldDate } from "@/utils/date-utils";
import { useTranslation } from "react-i18next";

export interface MDateInputFieldProps
  extends Omit<MInputFieldProps, "type" | "onClick" | "readOnly" | "value"> {
  /**
   * Initial value for the picker drawer (used when value is undefined)
   */
  initialPickerValue?: Date;
  /**
   * Minimum year allowed for selection (year-only mode)
   */
  minYear?: number;
  /**
   * If true, only show year picker (for year-only selection)
   */
  showYearOnly?: boolean;
  /**
   * The currently selected date
   */
  value?: Date;

  /**
   * Callback when date is selected
   */
  onDateChange?: (date: Date) => void;

  /**
   * Title for the date picker drawer
   */
  datePickerTitle?: string;

  /**
   * Minimum date allowed for selection
   */
  minDate?: Date;

  /**
   * Maximum date allowed for selection
   */
  maxDate?: Date;

  /**
   * Whether to hide the day selection in the date picker
   */
  hideDate?: boolean;

  /**
   * Format function for the displayed date
   */
  formatDate?: (date?: Date) => string;
}

/**
 * MDateInputField combines MInputField with MDatePicker for a seamless date selection experience.
 * It displays a text field with a calendar icon that opens a date picker drawer when clicked.
 */
export const MDateInputField: React.FC<MDateInputFieldProps> = ({
  value,
  onDateChange,
  datePickerTitle,
  minDate,
  maxDate,
  minYear,
  initialPickerValue,
  hideDate = false,
  showYearOnly = false,
  formatDate = inputFieldDate,
  icon = <IconCalendarWeekFilled />,
  showIcon = true,
  ...inputFieldProps
}) => {
  const { t } = useTranslation();
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const handleOpenDatePicker = () => {
    setIsDatePickerOpen(true);
  };

  const handleCloseDatePicker = () => {
    setIsDatePickerOpen(false);
  };

  const handleDateSelected = (date: Date) => {
    if (onDateChange) {
      onDateChange(date);
    }
    handleCloseDatePicker();
  };

  return (
    <>
      <MInputField
        {...inputFieldProps}
        type="text"
        value={formatDate(value) || ""}
        onClick={handleOpenDatePicker}
        icon={icon}
        showIcon={showIcon}
        onIconClick={handleOpenDatePicker}
        readOnly
      />

      <MDatePicker
        open={isDatePickerOpen}
        onClose={handleCloseDatePicker}
        onDone={handleDateSelected}
        initialValue={value ?? initialPickerValue}
        title={datePickerTitle || t("common.selectDate")}
        minDate={minDate}
        maxDate={maxDate}
        minYear={minYear}
        hideDate={hideDate}
        showYearOnly={showYearOnly}
      />
    </>
  );
};

export default MDateInputField;
